
// Top Rates

import React, { useEffect, useState } from "react";
import {useAuth0, withAuthenticationRequired} from "@auth0/auth0-react";
// import Loading from "../components/Loading";
// import LaneService, {getTopLanes} from "../services/LaneService";
import DataTable from 'react-data-table-component';
import LaneService from "../services/LaneService";
import {formatValue} from "react-currency-input-field";
import {Tooltip as ReactTooltip} from "react-tooltip";
import {PageLoader} from "./PageLoader";
import {initAmplitude, sendAmplitudeData, setAmplitudeUserId} from "../common/amplitude";
import "../styles/TopLanes.css";

// Helper functions moved outside for reuse and clarity
const financial = (x) => Number.parseFloat(x).toFixed(2);
const financialNoDecimals = (x) => Number.parseFloat(x).toFixed(0);

// State name to abbreviation mapping
const stateAbbreviations = {
    'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
    'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
    'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
    'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
    'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
    'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
    'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
    'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
    'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
    'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
};

const getStateAbbreviation = (stateName) => {
    return stateAbbreviations[stateName] || stateName;
};

export const TopLanesTable = () => {

    const [state, setState] = useState({
        showResult: false,
        apiMessage: undefined,
        error: null,
        subscription: false,
        subscriptionType: 'Default'
    });

    const {
        user,
        getAccessTokenSilently,
        loginWithPopup,
        getAccessTokenWithPopup,
    } = useAuth0();

    useEffect(() => {
        initAmplitude();
        if (user?.email) {
            setAmplitudeUserId(user.email);
        }
        sendAmplitudeData("Top Lanes", {});
        // eslint-disable-next-line
    }, [user]);

    const customStyles = {
        rows: {
            style: {
                // minHeight: '50px',
                // fontSize: '14px',
                // height: '50px',
                // border: 'none',
                // margin: '0',
                // padding: '0',
                // backgroundColor: 'white',
            }
        },

        headCells: {},

        cells: {},
        table: {
            style: {
                borderCollapse: 'collapse',
                backgroundColor: 'white',
                marginTop: '0px',
                marginBottom: '0px',
                tableLayout: 'auto',
                width: '100%',
                minWidth: '1200px',
            },
        },
    };

    const columns = [
        {
            name: 'EQUIPMENT',
            selector: row => row.equipment,
            sortable: true,
            minWidth: '120px',
            cell: row => <div className="top-routes-text">{row.equipment}</div>
        },
        {
            name: 'PICK CITY',
            selector: row => row.pickCity + ', ' + getStateAbbreviation(row.pickState),
            sortable: true,
            minWidth: '140px',
            cell: row => <div className="top-routes-text">{row.pickCity + ', ' + getStateAbbreviation(row.pickState)}</div>
        },
        {
            name: 'DROP CITY',
            selector: row => row.endCity + ', ' + getStateAbbreviation(row.endState),
            sortable: true,
            minWidth: '140px',
            cell: row => <div className="top-routes-text">{row.endCity + ', ' + getStateAbbreviation(row.endState)}</div>
        },
        {
            id: 'lane',
            name: 'LINE HAUL RATE',
            selector: row => '$' + financial(row.laneAmount),
            sortable: true,
            minWidth: '120px',
            cell: row => <div className="top-routes-dollar-text">${financialNoDecimals(row.laneAmount)}</div>
        },
        {
            id: 'pricePerMile',
            name: 'PRICE/MILE',
            selector: row => '$' + financial(row.pricePerMile),
            sortable: true,
            minWidth: '100px',
            cell: row => <div className="top-routes-dollar-text">${financial(row.pricePerMile)}</div>
        },
        {
            id: 'thirtyDays',
            name: '30 DAYS',
            selector: row => {
                const costPerMile = row.laneStatistics?.thirtyDaysAverage?.costPerMile;
                if (!costPerMile || costPerMile === 0) return '$0.00';
                return `$${Number(costPerMile).toFixed(2)}`;
            },
            sortable: true,
            minWidth: '120px',
            cell: row => {
                const costPerMile = row.laneStatistics?.thirtyDaysAverage?.costPerMile;
                const averageLoadRate = row.laneStatistics?.thirtyDaysAverage?.averageLoadRate;
                const isZero = !costPerMile || costPerMile === 0;
                const formattedCostPerMile = isZero ? '0.00' : Number(costPerMile).toFixed(2);
                const formattedLoadRate = !averageLoadRate || averageLoadRate === 0 ? '0' : Number(averageLoadRate).toFixed(0);
                
                return (
                    <div className={`top-routes-dollar-text ${isZero ? 'zero-value' : ''}`}>
                        <div>${formattedCostPerMile} / mile</div>
                        <div>${formattedLoadRate} / load</div>
                    </div>
                );
            }
        },
        {
            id: 'fifteenDays',
            name: '15 DAYS',
            selector: row => {
                const costPerMile = row.laneStatistics?.fifteenDaysAverage?.costPerMile;
                if (!costPerMile || costPerMile === 0) return '$0.00';
                return `$${Number(costPerMile).toFixed(2)}`;
            },
            sortable: true,
            minWidth: '120px',
            cell: row => {
                const costPerMile = row.laneStatistics?.fifteenDaysAverage?.costPerMile;
                const averageLoadRate = row.laneStatistics?.fifteenDaysAverage?.averageLoadRate;
                const isZero = !costPerMile || costPerMile === 0;
                const formattedCostPerMile = isZero ? '0.00' : Number(costPerMile).toFixed(2);
                const formattedLoadRate = !averageLoadRate || averageLoadRate === 0 ? '0' : Number(averageLoadRate).toFixed(0);
                
                return (
                    <div className={`top-routes-dollar-text ${isZero ? 'zero-value' : ''}`}>
                        <div>${formattedCostPerMile} / mile</div>
                        <div>${formattedLoadRate} / load</div>
                    </div>
                );
            }
        },
        {
            id: 'sevenDays',
            name: '7 DAYS',
            selector: row => {
                const costPerMile = row.laneStatistics?.sevenDaysAverage?.costPerMile;
                if (!costPerMile || costPerMile === 0) return '$0.00';
                return `$${Number(costPerMile).toFixed(2)}`;
            },
            sortable: true,
            minWidth: '120px',
            cell: row => {
                const costPerMile = row.laneStatistics?.sevenDaysAverage?.costPerMile;
                const averageLoadRate = row.laneStatistics?.sevenDaysAverage?.averageLoadRate;
                const isZero = !costPerMile || costPerMile === 0;
                const formattedCostPerMile = isZero ? '0.00' : Number(costPerMile).toFixed(2);
                const formattedLoadRate = !averageLoadRate || averageLoadRate === 0 ? '0' : Number(averageLoadRate).toFixed(0);
                
                return (
                    <div className={`top-routes-dollar-text ${isZero ? 'zero-value' : ''}`}>
                        <div>${formattedCostPerMile} / mile</div>
                        <div>${formattedLoadRate} / load</div>
                    </div>
                );
            }
        },
        {
            id: 'threeDays',
            name: '3 DAYS',
            selector: row => Number(row.laneStatistics?.threeDaysAverage?.costPerMile || 0),
            sortable: true,
            minWidth: '120px',
            cell: row => {
                const costPerMile = row.laneStatistics?.threeDaysAverage?.costPerMile;
                const averageLoadRate = row.laneStatistics?.threeDaysAverage?.averageLoadRate;
                const isZero = !costPerMile || costPerMile === 0;
                const formattedCostPerMile = isZero ? '0.00' : Number(costPerMile).toFixed(2);
                const formattedLoadRate = !averageLoadRate || averageLoadRate === 0 ? '0' : Number(averageLoadRate).toFixed(0);
                
                return (
                    <div className={`top-routes-dollar-text ${isZero ? 'zero-value' : ''}`}>
                        <div>${formattedCostPerMile} / mile</div>
                        <div>${formattedLoadRate} / load</div>
                    </div>
                );
            }
        },
        {
            id: 'yesterday',
            name: 'YESTERDAY',
            selector: row => {
                const costPerMile = row.laneStatistics?.oneDaysAverage?.costPerMile;
                if (!costPerMile || costPerMile === 0) return '$0.00';
                return `$${Number(costPerMile).toFixed(2)}`;
            },
            sortable: true,
            minWidth: '120px',
            cell: row => {
                const costPerMile = row.laneStatistics?.oneDaysAverage?.costPerMile;
                const averageLoadRate = row.laneStatistics?.oneDaysAverage?.averageLoadRate;
                const isZero = !costPerMile || costPerMile === 0;
                const formattedCostPerMile = isZero ? '0.00' : Number(costPerMile).toFixed(2);
                const formattedLoadRate = !averageLoadRate || averageLoadRate === 0 ? '0' : Number(averageLoadRate).toFixed(0);
                
                return (
                    <div className={`top-routes-dollar-text ${isZero ? 'zero-value' : ''}`}>
                        <div>${formattedCostPerMile} / mile</div>
                        <div>${formattedLoadRate} / load</div>
                    </div>
                );
            }
        },
    ];

    let data = [];

    const callApi = async () => {
        try {
            const token = await getAccessTokenSilently();
            const response = await LaneService.getCityOptionsTwo(user.sub, token);
            response.collection.sort((a, b) => parseFloat(b.laneAmount) - parseFloat(a.laneAmount));
            setState(prev => ({
                ...prev,
                showResult: true,
                apiMessage: response.collection,
                subscription: response.subscription.status,
                subscriptionType: response.subscription.subscriptionType
            }));
        } catch (error) {
            setState(prev => ({
                ...prev,
                error: error.error,
            }));
        }
    };

    useEffect(() => {
        callApi();
        // eslint-disable-next-line
    }, []);

    return (
        <div>
            <div className="bg-image">
                {!state.showResult && (
                    <div className="p-3">
                        <PageLoader></PageLoader>
                    </div>
                )}

                {state.showResult && <div>
                    <div className="container-fluid">
                        <div className="top-lanes-description">
                            <p className="dashboard-text" style={{textAlign: 'left', margin: '0'}}>Discover the most profitable freight lanes in the United States. This comprehensive analysis showcases the top 50+ routes with the highest rate per mile, providing real-time insights into market trends and revenue opportunities over the past 30 days. This list refreshes every 24 hours to ensure you have the most current market data.</p>
                        </div>
                        <header className="top-routes-text">
                            <DataTable
                                customStyles={customStyles}
                                columns={columns}
                                data={state.apiMessage}
                                defaultSortFieldId="threeDays"
                                defaultSortAsc={false}
                                direction="auto"
                                fixedHeaderScrollHeight="300px"
                                pagination
                                responsive
                                subHeaderAlign="left"
                                subHeaderWrap
                                theme="custom"
                                data-testid="table"
                                className="top-lanes-table  top-rates-table"
                                noTableHead={false}
                                persistTableHead={true}
                            />
                        </header>
                        <br/><br/><br/>
                    </div>
                </div>}
            </div>
        </div>
    );
};

export default withAuthenticationRequired(TopLanesTable, {
    onRedirecting: () => <PageLoader />,
});